"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from "@microsoft/signalr";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { NotificationEntity } from "@/components/Classes/NotificationEntity";
import { GetSessionCookie, GetCustomTokenFromSession } from "@/services/Cookies/FirebaseSession";
import { notificationService } from "@/services/APIs/notificationService";
import {
  isPushNotificationSupported,
  subscribeToPushNotifications,
  sendSubscriptionToServer,
} from "@/lib/pushNotifications";

interface NotificationContextType {
  notifications: NotificationEntity[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  connection: HubConnection | null;
  isLoading: boolean;
  markAllAsRead: () => Promise<void>;
  notificationPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  pushSubscription: PushSubscription | null;
  subscribeToPush: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [notifications, setNotifications] = useState<NotificationEntity[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const lastSoundPlayedAt = useRef<number>(0);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");
  const [pushSubscription, setPushSubscription] =
    useState<PushSubscription | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const connectionRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only initialize audio context after user is logged in
    const initializeNotifications = async () => {
      const sessionToken = await GetSessionCookie();
      
      // Don't initialize if no session (user not logged in)
      if (!sessionToken) {
        return;
      }

      // Check notification API availability and permission
      if ("Notification" in window) {
        setNotificationPermission(Notification.permission);

        // Debug notification support
        //debugNotificationSupport();

        // Auto-setup push notifications if supported and permission granted
        if (
          Notification.permission === "granted" &&
          isPushNotificationSupported()
        ) {
          subscribeToPush()
            .then((success) => {
              if (success) {
                //console.log("Push notifications set up automatically");
              }
            })
            .catch((error) => {
              console.error("Error setting up push notifications:", error);
            });
        }
      } else {
        console.warn("This browser does not support notifications");
      }
    };

    initializeNotifications();

    // Listen for messages from service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "ADD_NOTIFICATION_FROM_PUSH") {
        console.log(
          "Received notification from service worker:",
          event.data.notification
        );
        const notification = event.data.notification;

        // Convert dates from ISO strings to Date objects
        const notificationEntity: NotificationEntity = {
          ...notification,
          createdAt: new Date(notification.createdAt),
          lastUpdatedAt: new Date(notification.lastUpdatedAt),
          expiryDate: notification.expiryDate
            ? new Date(notification.expiryDate)
            : undefined,
        };

        // Add notification to the state (unread count is derived below)
        setNotifications((prev) => {
          // Check if notification already exists to avoid duplicates
          const exists = prev.some((n) => n.id === notificationEntity.id);
          if (!exists) {
            return [notificationEntity, ...prev];
          }
          return prev;
        });

        // Play sound for new notification
        playNotificationSound();
      } else if (event.data && event.data.type === "NAVIGATE_TO") {
        // Handle navigation from service worker
        console.log("Navigating to:", event.data.url);
        // You can implement navigation logic here if needed
        // For example: router.push(event.data.url);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      }
    };
  }, []);

  const playNotificationSound = () => {
    // Rate-limit: play at most once per window (default 60 seconds) to avoid
    // repeated sounds during notification bursts. Adjust PLAY_WINDOW_MS as needed.
    const PLAY_WINDOW_MS = 60000; // 60 seconds
    const now = Date.now();

    if (lastSoundPlayedAt.current && now - lastSoundPlayedAt.current < PLAY_WINDOW_MS) {
      // Suppress sound because we already played recently
      // eslint-disable-next-line no-console
      console.debug(
        "[Notifications] playNotificationSound suppressed (within rate limit)",
        { lastPlayed: new Date(lastSoundPlayedAt.current).toISOString(), now: new Date(now).toISOString() }
      );
      return;
    }

    try {
      // Create a softer, shorter notification sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      const startTime = audioContext.currentTime;
      const noteDuration = 0.18; // gentle duration

      // Main gentle tone
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(600, startTime);

      // Very soft amplitude envelope
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.08, startTime + 0.03); // soft fade in
      gain.gain.exponentialRampToValueAtTime(0.0005, startTime + noteDuration); // smooth fade out

      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start(startTime);
      osc.stop(startTime + noteDuration);

      // Subtle harmonic to add warmth (lower amplitude)
      const subOsc = audioContext.createOscillator();
      const subGain = audioContext.createGain();
      subOsc.type = "sine";
      subOsc.frequency.setValueAtTime(420, startTime + 0.05);
      subGain.gain.setValueAtTime(0, startTime + 0.05);
      subGain.gain.linearRampToValueAtTime(0.04, startTime + 0.07);
      subGain.gain.exponentialRampToValueAtTime(0.0005, startTime + noteDuration + 0.02);

      subOsc.connect(subGain);
      subGain.connect(audioContext.destination);
      subOsc.start(startTime + 0.05);
      subOsc.stop(startTime + noteDuration + 0.02);

      lastSoundPlayedAt.current = now;
      // Debug log so it's easy to confirm when sound is triggered
      // (will appear in DevTools console)
      // eslint-disable-next-line no-console
      console.debug("[Notifications] playNotificationSound triggered", new Date(lastSoundPlayedAt.current).toISOString());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error playing notification sound:", error);
    }
  };

  const showBrowserNotification = useCallback(
    (notification: NotificationEntity) => {
      // Check if notifications are supported and permitted
      if (!("Notification" in window)) {
        console.warn("This browser does not support desktop notifications");
        return;
      }

      if (Notification.permission !== "granted") {
        console.warn(
          "Notification permission not granted:",
          Notification.permission
        );
        return;
      }

      // Check if running in secure context (required for some browsers)
      if (!window.isSecureContext && location.protocol !== "http:") {
        console.warn("Notifications may not work in insecure context");
      }

      try {
        // Create notification with browser-compatible options
        const notificationOptions: NotificationOptions = {
          body: notification.message,
          icon: "/favicon.ico",
          // Remove options that might not be supported in all browsers
          tag: notification.id, // Prevents duplicate notifications
          requireInteraction: false,
        };

        // Safari compatibility: Some options cause issues in Safari
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );
        if (!isSafari) {
          notificationOptions.badge = "/favicon.ico";
          notificationOptions.silent = false;
        }

        const browserNotification = new Notification(
          notification.title || "New Notification",
          notificationOptions
        );

        console.log("Browser notification created successfully:", {
          browser: isSafari ? "Safari" : "Other",
          permission: Notification.permission,
          secure: window.isSecureContext,
        });

        // Handle notification click
        browserNotification.onclick = () => {
          window.focus(); // Bring the browser tab to focus
          browserNotification.close();
          // You can add additional logic here, like navigating to a specific page
        };

        // Handle notification error
        browserNotification.onerror = (event) => {
          console.error("Notification error:", event);
        };

        // Auto-close after 5 seconds
        setTimeout(() => {
          browserNotification.close();
        }, 5000);
      } catch (error) {
        console.error("Failed to create browser notification:", error);
      }
    },
    []
  );

  const requestNotificationPermission =
    async (): Promise<NotificationPermission> => {
      if (!("Notification" in window)) {
        console.warn("This browser does not support notifications");
        return "denied";
      }

      // Check if already granted
      if (Notification.permission === "granted") {
        return "granted";
      }

      // Check if permission was already denied
      if (Notification.permission === "denied") {
        console.warn("Notification permission was previously denied");
        return "denied";
      }

      try {
        // Request permission
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);

        // console.log("Notification permission result:", {
        //   permission,
        //   browser: navigator.userAgent,
        //   secure: window.isSecureContext,
        // });

        return permission;
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        return "denied";
      }
    };

  const startHeartbeat = (connection: HubConnection) => {
    // Clear any existing heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Send heartbeat every 30 seconds (half the server timeout of 2 minutes)
    heartbeatIntervalRef.current = setInterval(async () => {
      if (connection.state === HubConnectionState.Connected) {
        try {
          await connection.invoke("Heartbeat");
          // console.log("Heartbeat sent successfully");
        } catch (error) {
          console.error("Error sending heartbeat:", error);
        }
      }
    }, 30000); // 30 seconds
  };

  useEffect(() => {
		let disposed = false;
    const setupConnection = async () => {
			if (disposed) return;
      // Access token resolver for SignalR: prefer Firebase ID token (ID token issuer
      // is https://securetoken.google.com/{projectId}) so JwtBearer configured for
      // Firebase ID tokens will validate the handshake. Fall back to session cookie
      // token if an ID token is not available on the client.
      const getAccessTokenForSignalR = async (): Promise<string | null> => {
        try {
          // Try Firebase client SDK first (ID token)
          const auth = getAuth();
          let user = auth.currentUser;

          // If no user in client SDK, try to sign in using session cookie
          if (!user) {
            const customToken = await GetCustomTokenFromSession();
            if (customToken) {
              try {
                const userCredential = await signInWithCustomToken(
                  auth,
                  customToken
                );
                user = userCredential.user;
              } catch (signInError) {
                console.error(
                  "Error signing in with custom token:",
                  signInError
                );
              }
            }
          }

          if (user) {
            // Force refresh false to use cached token when valid; set true to force
            // refresh if you need the freshest token: user.getIdToken(true)
            const idToken = await user.getIdToken();
            if (idToken) return idToken;
          }
        } catch (err) {
          // ignore and fallback to session cookie
          // console.debug("Firebase ID token fetch failed:", err);
        }

        return null;
      };

      const tokenForConnection = await getAccessTokenForSignalR();

      if (!tokenForConnection) {
			// Firebase/session hydration can finish after this provider first mounts.
			connectionRetryRef.current = setTimeout(setupConnection, 3000);
        return;
      }

      const newConnection = new HubConnectionBuilder()
        .withUrl(`${process.env.NEXT_PUBLIC_BACKEND}notificationHub`, {
          // Use the resolver so reconnects can obtain fresh tokens when needed
          accessTokenFactory: getAccessTokenForSignalR,
        })
        .withAutomaticReconnect()
        .build();

      try {
        await newConnection.start();
        setConnection(newConnection);
        //console.log("SignalR connection established successfully");

        newConnection.on(
          "ReceiveNotification",
          (notification: NotificationEntity) => {
            //console.log("Received notification via SignalR:", notification);
            setNotifications((prev) => [notification, ...prev]);
            playNotificationSound(); // Play sound when new notification arrives
            showBrowserNotification(notification); // Show browser notification
          }
        );

        // Handle missed notifications when reconnecting
        newConnection.on(
          "ReceiveMissedNotifications",
          (missedNotifications: NotificationEntity[]) => {
            //console.log("Received missed notifications:", missedNotifications);
            if (missedNotifications && missedNotifications.length > 0) {
              setNotifications((prev) => {
                // Merge missed notifications with existing ones, avoiding duplicates
                const existingIds = new Set(prev.map((n) => n.id));
                const newNotifications = missedNotifications.filter(
                  (n) => !existingIds.has(n.id)
                );
                return [...newNotifications, ...prev];
              });

                  // NOTE: Do NOT play sound for missed notifications delivered
                  // on reconnect or merge. The sound should only play when a
                  // new real-time notification arrives (SignalR `ReceiveNotification`)
                  // or from a push message handled by the service worker.
            }
          }
        );

        // Handle connection errors
        newConnection.onclose((error) => {
          //console.error("SignalR connection closed:", error);
          // Clear heartbeat when connection closes
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
        });

        newConnection.onreconnecting((error) => {
          console.warn("SignalR reconnecting:", error);
          // Clear heartbeat during reconnection
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
        });

        newConnection.onreconnected((connectionId) => {
          //console.log("SignalR reconnected:", connectionId);
          // Restart heartbeat when reconnected
          startHeartbeat(newConnection);
        });

        // Start sending heartbeats
        startHeartbeat(newConnection);
      } catch (error) {
        console.error("SignalR connection error:", error);
			if (!disposed) {
				connectionRetryRef.current = setTimeout(setupConnection, 5000);
			}
      }
    };

    setupConnection();
    return () => {
			disposed = true;
			if (connectionRetryRef.current) {
				clearTimeout(connectionRetryRef.current);
				connectionRetryRef.current = null;
			}
      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      if (connection) {
        console.log("Stopping SignalR connection");
        connection.stop();
      }
    };
  }, [showBrowserNotification]); // Add showBrowserNotification as dependency

  useEffect(() => {
    const loadInitialNotifications = async () => {
      try {
        // Check if user is logged in before loading notifications
        const sessionToken = await GetSessionCookie();
        if (!sessionToken) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const notifications = await notificationService.getUserNotifications();
        setNotifications(notifications);
        const unreadCount = notifications.filter((n) => !n.isRead).length;
        // Do NOT play sound for existing/unread notifications loaded on
        // initial page load. Sounds should only play for incoming
        // real-time notifications or push messages from the service worker.
      } catch (error) {
        console.error("Error loading initial notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialNotifications();
  }, []);

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === id
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== id)
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, isRead: true }))
      );
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const subscribeToPush = async (): Promise<boolean> => {
    try {
      // First request notification permission
      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        console.warn(
          "Notification permission denied, cannot subscribe to push"
        );
        return false;
      }

      // Check if push notifications are supported
      if (!isPushNotificationSupported()) {
        console.warn("Push notifications not supported in this browser");
        return false;
      }

      // Subscribe to push notifications
      const subscription = await subscribeToPushNotifications();
      if (!subscription) {
        console.error("Failed to get push subscription");
        return false;
      }

      setPushSubscription(subscription);

      // Send subscription to server
      const success = await sendSubscriptionToServer(subscription);
      if (!success) {
        console.error("Failed to send subscription to server");
        return false;
      }

      //console.log("Successfully subscribed to push notifications");
      return true;
    } catch (error) {
      console.error("Error subscribing to push notifications:", error);
      return false;
    }
  };

  // Enhanced browser detection for push support
  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor);
    const isSafari =
      /Safari/.test(ua) && /Apple Computer/.test(navigator.vendor);
    const isEdge = /Edg/.test(ua);
    const isFirefox = /Firefox/.test(ua);

    return {
      isChrome,
      isSafari,
      isEdge,
      isFirefox,
      userAgent: ua,
      isSecureContext: window.isSecureContext,
      protocol: location.protocol,
      pushSupported: isPushNotificationSupported(),
    };
  };

  // Debug notification support
  const debugNotificationSupport = () => {
    const browserInfo = getBrowserInfo();
    console.log("Browser notification debug:", {
      ...browserInfo,
      notificationSupported: "Notification" in window,
      permission:
        "Notification" in window ? Notification.permission : "not supported",
      serviceWorkerSupported: "serviceWorker" in navigator,
    });
  };

  // Keep unreadCount authoritative and derived from the notifications array to
  // avoid race conditions and stale increments/decrements.
  useEffect(() => {
    const count = notifications.filter((n) => !n.isRead).length;
    setUnreadCount(count);
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        deleteNotification,
        connection,
        isLoading,
        markAllAsRead,
        notificationPermission,
        requestNotificationPermission,
        pushSubscription,
        subscribeToPush,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
